/**
 * Unsaved employee forms kept until they are saved or discarded (#270): «Behold utkast» closes the
 * editor and the draft is offered again the next time that person's editor opens, as are drafts left
 * by a refresh, a closed panel or Back. No retention policy exists for drafts in the app, so the
 * narrowest one is used: this tab's session storage only, keyed by company, signed-in user and
 * person, never the fødselsnummer, and every draft is dropped at logout. Another company or another
 * user on this browser can never restore one.
 */
const PREFIX = 'timefly.utkast.';

export interface Draft<T> {
  at: string;
  form: T;
}
const key = (tenant: number, user: number, person: number | 'ny') => `${PREFIX}${tenant}.${user}.${person}`;

export function saveDraft(
  tenant: number | undefined,
  user: number | undefined,
  person: number | 'ny',
  form: object,
) {
  if (tenant == null || user == null) return;
  // the fødselsnummer is never written down, at whatever depth the form holds it
  const text = JSON.stringify({ at: new Date().toISOString(), form }, (k, v) =>
    k === 'personal_number' ? undefined : v,
  );
  try {
    sessionStorage.setItem(key(tenant, user, person), text);
  } catch {
    /* private mode or full: the draft lives only while the editor is open */
  }
}
export function loadDraft<T>(
  tenant: number | undefined,
  user: number | undefined,
  person: number | 'ny',
): Draft<T> | null {
  if (tenant == null || user == null) return null;
  try {
    return JSON.parse(sessionStorage.getItem(key(tenant, user, person)) ?? 'null');
  } catch {
    return null;
  }
}
export function dropDraft(tenant: number | undefined, user: number | undefined, person: number | 'ny') {
  if (tenant == null || user == null) return;
  try {
    sessionStorage.removeItem(key(tenant, user, person));
  } catch {
    /* nothing kept */
  }
}
/** At logout: nobody who signs in next on this tab inherits a form. */
export function forgetDrafts() {
  try {
    for (const k of Object.keys(sessionStorage)) if (k.startsWith(PREFIX)) sessionStorage.removeItem(k);
  } catch {
    /* nothing kept */
  }
}
